import Stack from "@mui/joy/Stack";
import Grid from "@mui/joy/Grid";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import ModalClose from "@mui/joy/ModalClose";
import DialogTitle from "@mui/joy/DialogTitle";
import DialogActions from "@mui/joy/DialogActions";
import DialogContent from "@mui/joy/DialogContent";
import Alert from "@mui/joy/Alert";
import Button from "@mui/joy/Button";
import Typography from "@mui/joy/Typography";
import Badge from "@mui/joy/Badge";
import Textarea from "@mui/joy/Textarea";
import SpeakerNotesIcon from "@mui/icons-material/SpeakerNotesOutlined";
import {
  GetIfInvoicesResponseObj,
  GetInterfaceInvoicesResponseObj,
  GetInvoiceDetailsResponseObj,
  IFInvoiceWithDetailsObj,
  InterfaceInvoice,
} from "@/types/interfaces/crm";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient, UseQueryResult } from "@tanstack/react-query";
import Box from "@mui/system/Box";
import Loader from "@/components/Loader/Loader";
import Person2Icon from "@mui/icons-material/Person2";
import BookOnlineIcon from "@mui/icons-material/BookOnline";
import NumbersIcon from "@mui/icons-material/Numbers";
import AutoRenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import GroupsIcon from "@mui/icons-material/Groups";
import StarRateIcon from "@mui/icons-material/StarRate";
import ArchiveIcon from "@mui/icons-material/Archive";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import copy from "copy-text-to-clipboard";
import { decodeAndFormatHtml } from "@/utils/decodeAndFormatHtml";
import { HEADER_HEIGHT, NAVBAR_HEIGHT } from "@/types/constants/common";
import { INVOICES_SIDEBAR_WIDTH } from "../sidebar/InvoicesSidebar";
import { ValidationResponse } from "../../utility/validationChecker";
import { useSearchParams } from "react-router-dom";
import axiosInstance from "@/services/api/axiosInstance";
import { toast } from "sonner";
import useAuthStore from "@/zustand/authStore";
import { formatInTorontoTime } from "../../utility/utils";
import { getOneInterfaceInvoice, updateInterfaceInvoice } from "@/services/api/crm/financeQa";
import Chip from "@mui/joy/Chip";
import IconButton from "@mui/joy/IconButton";
import Tooltip from "@mui/joy/Tooltip";
import { INTERFACE_TEAM_MEMBERS } from "@/types/constants/interfaceTeamMembers";
import { getAgentRisingStarLevel } from "@/services/api/interface-validator/risingStar";

const invoiceHeaderFields: {
  field: string;
  access: (data: GetInvoiceDetailsResponseObj) => string | number;
  Icon: any;
}[] = [
  {
    field: "Profile Name",
    access: (data: GetInvoiceDetailsResponseObj) => data?.data?.ifInvoice?.profileName || "_",
    Icon: Person2Icon,
  },
  {
    field: "OS1 Agent",
    access: (data: GetInvoiceDetailsResponseObj) => {
      const profilesData = data?.references?.profilesData || [];
      const profileNumber = data?.data?.trip.osAgentNumber1;
      const agent = profilesData.find((p) => p.profileNumber === profileNumber);
      return agent ? agent.fullName : "_";
    },
    Icon: Person2Icon,
  },
  {
    field: "OS2 Agent",
    access: (data: GetInvoiceDetailsResponseObj) => {
      const profilesData = data?.references?.profilesData || [];
      const profileNumber = data?.data?.trip.osAgentNumber2;
      const agent = profilesData.find((p) => p.profileNumber === profileNumber);
      return agent ? agent.fullName : "_";
    },
    Icon: Person2Icon,
  },
  {
    field: "Branch No.",
    access: (data: GetInvoiceDetailsResponseObj) => data?.data?.ifInvoice?.branchNumber || "_",
    Icon: BookOnlineIcon,
  },
  {
    field: "Trip ID",
    access: (data: GetInvoiceDetailsResponseObj) => data?.data?.trip?.tripId?.toString() || "_",
    Icon: NumbersIcon,
  },
  {
    field: "Group Code",
    access: (data: GetInvoiceDetailsResponseObj) => data?.data?.trip?.groupCode || "_",
    Icon: GroupsIcon,
  },
  {
    field: "Agency Tier (CL)",
    access: (data: GetInvoiceDetailsResponseObj) => {
      const agentDetails = data?.references?.agentDetails || [];
      const tier = agentDetails.find((a) => a.primaryAgent)?.tierId;
      return tier || "_";
    },
    Icon: StarRateIcon,
  },
];

interface Props {
  invoices: IFInvoiceWithDetailsObj[];
  invoiceDetailsQueryClient: UseQueryResult<GetInvoiceDetailsResponseObj>;
  checkResults: ValidationResponse[];
  setSelectedInvoiceIndex: (index: number) => void;
  selectedInvoiceIndex: number;
}

export default function InvoiceHeader({
  invoices,
  invoiceDetailsQueryClient,
  checkResults,
  selectedInvoiceIndex,
  setSelectedInvoiceIndex,
}: Props) {
  const user = useAuthStore((state) => state.user);
  const [remarksModalOpen, setRemarksModalOpen] = useState(false);
  const [postConfirmOpen, setPostConfirmOpen] = useState(false);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [isTogglePostLoading, setIsTogglePostLoading] = useState(false);
  const [isUpdatingNotes, setIsUpdatingNotes] = useState(false);
  const [isTogglingParked, setIsTogglingParked] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const invoiceId = searchParams.get("invoiceId");

  const getOneInterfaceInvoiceQuery = useQuery({
    queryKey: ["interfaceInvoices", invoiceId],
    enabled: !!invoiceId,
    queryFn: () => getOneInterfaceInvoice(invoiceId!),
  });

  const primaryAgentNumber =
    invoiceDetailsQueryClient?.data?.data?.trip.osAgentNumber2 ||
    invoiceDetailsQueryClient?.data?.data?.trip.osAgentNumber1;

  const risingStarQuery = useQuery({
    queryKey: ["risingStarLevel", primaryAgentNumber],
    enabled: !!primaryAgentNumber,
    queryFn: () => getAgentRisingStarLevel(primaryAgentNumber!),
  });

  useEffect(() => {
    if (getOneInterfaceInvoiceQuery.data) {
      const notes = getOneInterfaceInvoiceQuery.data.data?.notes;
      setNoteText(notes || "");
    }
  }, [getOneInterfaceInvoiceQuery.data, invoiceId]);

  const hasErrors = checkResults.some((result) => !result.isValid);

  const queryClient = useQueryClient();

  const data = (invoiceDetailsQueryClient?.data as GetInvoiceDetailsResponseObj)?.data;
  const invoice = data?.ifInvoice;
  const isGroupBooking = (invoiceDetailsQueryClient?.data as GetInvoiceDetailsResponseObj)?.references?.isGroupBooking;
  const ableToPost = !invoice?.postNow; //postNow true means ready to post on CL, so button should show "" and vice versa

  const interfaceStatus = getOneInterfaceInvoiceQuery.data?.data?.status;
  const isParked = interfaceStatus === "parked";
  const isPosted = interfaceStatus === "posted";

  const containsBSP = useMemo(() => {
    const reservations = data?.reservations || [];
    return reservations.some((res) => res.reservation?.productCode === 0);
  }, [data?.reservations]);

  const createdTime = useMemo(
    () => (invoice?.createdTime ? new Date(invoice?.createdTime) : null),
    [invoice?.createdTime],
  );

  const travelDate = useMemo(() => {
    const dateStr = (invoiceDetailsQueryClient?.data as GetInvoiceDetailsResponseObj)?.data?.trip?.travelDate;
    return dateStr ? new Date(dateStr) : null;
  }, [invoiceDetailsQueryClient?.data]);

  const openNotesModal = () => {
    setNotesModalOpen(true);
  };

  const handleUpdateNotes = async () => {
    if (!invoice) return;

    setIsUpdatingNotes(true);
    try {
      const updated = await updateInterfaceInvoice(invoice.invoiceId, {
        notes: noteText,
        ...(invoice.invoiceNumber ? { clInvoiceNumber: invoice.invoiceNumber } : {}),
        ...(createdTime ? { clCreatedTime: createdTime.toISOString() } : {}),
      });

      queryClient.setQueryData(["interfaceInvoices", invoiceId], (oldData: { data: InterfaceInvoice | null } | undefined) => {
        if (!oldData?.data) return updated;
        return {
          data: {
            ...oldData.data,
            notes: noteText,
          },
        };
      });
      toast.success("Internal notes updated successfully");
      setNotesModalOpen(false);
    } catch (error) {
      const apiErrorMessage = (error as any)?.response?.data?.error;
      toast.error(apiErrorMessage || (error as Error).message || "Something went wrong while updating notes");
    } finally {
      setIsUpdatingNotes(false);
    }
  };

  const handleToggleParked = async () => {
    if (!invoice) return;

    const nextStatus: InterfaceInvoice["status"] = isParked ? "pending" : "parked";
    setIsTogglingParked(true);
    try {
      const updated = await updateInterfaceInvoice(invoice.invoiceId, {
        status: nextStatus,
        ...(invoice.invoiceNumber ? { clInvoiceNumber: invoice.invoiceNumber } : {}),
        ...(createdTime ? { clCreatedTime: createdTime.toISOString() } : {}),
      });

      queryClient.setQueryData(["interfaceInvoices", invoiceId], (oldData: { data: InterfaceInvoice | null } | undefined) => {
        if (!oldData?.data) return updated;
        return {
          data: {
            ...oldData.data,
            status: nextStatus,
          },
        };
      });

      queryClient.setQueriesData<GetInterfaceInvoicesResponseObj>(
        { queryKey: ["crmParkedInterfaceInvoices"] },
        (oldData) => {
          if (!oldData) return oldData;
          const exists = oldData.invoices.some((inv) => inv.clInvoiceId === invoice.invoiceId);
          const invoices = exists
            ? oldData.invoices.map((inv) =>
                inv.clInvoiceId === invoice.invoiceId ? { ...inv, status: nextStatus } : inv,
              )
            : [...oldData.invoices, updated.data];
          return { ...oldData, invoices };
        },
      );
      toast.success(isParked ? "Invoice unparked" : "Invoice parked");
    } catch (error) {
      const apiErrorMessage = (error as any)?.response?.data?.error;
      toast.error(apiErrorMessage || (error as Error).message || "Something went wrong while updating park status");
    } finally {
      setIsTogglingParked(false);
    }
  };

  const openPostConfirm = () => {
    setPostConfirmOpen(true);
  };

  const handleConfirmPost = async () => {
    setIsTogglePostLoading(true);
    try {
      const res = await axiosInstance.post("/crm/admin/finance-qa/invoices/toggle-post", {
        postStatusBy: user!.ttandEmail,
        invoices: [
          {
            invoiceId,
            postNow: ableToPost,
          },
        ],
      });

      if (res.data) {
        const results = res.data?.data;
        if (results && results.length) {
          const result = results[0];
          if (!result.success) {
            throw new Error(result.message || "Failed to toggle post status");
          }
        } else {
          throw new Error("No response data received");
        }
      } else {
        throw new Error("No data received from server");
      }
      queryClient.setQueryData(
        ["interfaceValidatorInvoiceDetails", invoiceId],
        (oldData: GetInvoiceDetailsResponseObj | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              ifInvoice: {
                ...oldData.data.ifInvoice,
                postNow: ableToPost,
              },
            },
          };
        },
      );

      queryClient.setQueryData(["interfaceInvoices"], (oldData: GetIfInvoicesResponseObj | undefined) => {
        if (!oldData) return oldData;
        return {
          invoices: (oldData.invoices || []).map((invoice) => {
            if (invoice.invoiceId === parseInt(invoiceId || "", 10)) {
              return {
                ...invoice,
                postNow: ableToPost,
              };
            }
            return invoice;
          }),
          metaInfo: oldData.metaInfo,
        };
      });
    } catch (error) {
      const apiErrorMessage = (error as any)?.response?.data?.message;
      toast.error(apiErrorMessage || (error as Error).message || "Something went wrong while toggling post status");
    } finally {
      setPostConfirmOpen(false);
      setIsTogglePostLoading(false);
    }
  };

  function goToNextInvoice() {
    if (selectedInvoiceIndex < invoices.length - 1) {
      setSelectedInvoiceIndex(selectedInvoiceIndex + 1);
      setSearchParams({ invoiceId: invoices[selectedInvoiceIndex + 1].invoiceId?.toString() });
    }
  }

  function goToPreviousInvoice() {
    if (selectedInvoiceIndex > 0) {
      setSelectedInvoiceIndex(selectedInvoiceIndex - 1);
      setSearchParams({ invoiceId: invoices[selectedInvoiceIndex - 1].invoiceId?.toString() });
    }
  }

  function reload() {
    if (invoiceId) {
      invoiceDetailsQueryClient.refetch();
      queryClient.refetchQueries({
        queryKey: ["interfaceInvoices", invoiceId],
        exact: true,
      });
    }
  }

  return (
    <>
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
        sx={{
          padding: "1rem",
          borderBottom: "1px solid #e0e0e0",
          borderLeft: "1px solid #e0e0e0",
          position: "fixed",
          top: HEADER_HEIGHT + NAVBAR_HEIGHT,
          left: INVOICES_SIDEBAR_WIDTH,
          right: 0,
          height: "80px",
          backgroundColor: "white",
          zIndex: 10,
        }}
      >
        <Stack>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography fontFamily={"sans-serif"} fontWeight={600} fontSize={20} textColor="#334155">
              #{invoice?.record} | {invoice?.invoiceNumber}
            </Typography>
            {invoice?.invoiceNumber && (
              <Tooltip variant="outlined" title="Copy invoice number" arrow>
                <IconButton
                  variant="plain"
                  color="neutral"
                  size="sm"
                  onClick={() => {
                    if (copy(invoice.invoiceNumber!)) {
                      toast.success("Invoice number copied to your clipboard!");
                    }
                  }}
                >
                  <ContentCopyOutlinedIcon sx={{ fontSize: "18px" }} />
                </IconButton>
              </Tooltip>
            )}
            {getOneInterfaceInvoiceQuery.data?.data?.assignee && (
              <Chip variant="outlined" color="neutral" startDecorator={<Person2Icon />} size="sm">
                {INTERFACE_TEAM_MEMBERS.find(
                  (member) => member.email === getOneInterfaceInvoiceQuery.data?.data?.assignee,
                )?.name || getOneInterfaceInvoiceQuery.data?.data?.assignee}
              </Chip>
            )}
            {containsBSP && (
              <Chip variant="outlined" color="warning" size="sm">
                Contains BSP
              </Chip>
            )}
            {isGroupBooking && (
              <Chip variant="outlined" color="success" size="sm" startDecorator={<GroupsIcon />}>
                Group booking
              </Chip>
            )}
            {isParked && (
              <Chip variant="outlined" color="warning" size="sm" startDecorator={<ArchiveIcon />}>
                Parked
              </Chip>
            )}
          </Stack>
          <Typography fontFamily={"Inter"} fontWeight={400} fontSize={"12px"}>
            Transaction Date : {createdTime && formatInTorontoTime(createdTime, "MMM dd, yyyy h:mm a zzz")}
          </Typography>
          <Typography fontFamily={"Inter"} fontWeight={400} fontSize={"12px"}>
            Travel Date : {travelDate && formatInTorontoTime(travelDate, "MMM dd, yyyy h:mm a zzz")}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={2}>
          <Badge variant="solid" color="primary" invisible={!noteText}>
            <Button onClick={openNotesModal} variant="outlined" color="neutral">
              <SpeakerNotesIcon />
            </Button>
          </Badge>
          <Button onClick={reload} variant="outlined" color="neutral">
            <AutoRenewOutlinedIcon />
          </Button>
          <Tooltip
            variant="outlined"
            arrow
            title={
              isParked
                ? "Unpark this invoice"
                : invoice?.postNow
                ? "Unmark as ready before parking"
                : isPosted
                ? "Posted invoices can't be parked"
                : "Park this invoice so it won't be posted"
            }
          >
            <span>
              <Button
                loading={isTogglingParked}
                disabled={!isParked && (!!invoice?.postNow || isPosted || getOneInterfaceInvoiceQuery.isLoading)}
                startDecorator={<ArchiveIcon />}
                color={isParked ? "warning" : "neutral"}
                variant={isParked ? "soft" : "outlined"}
                onClick={handleToggleParked}
              >
                {isParked ? "Unpark" : "Park"}
              </Button>
            </span>
          </Tooltip>
          <Tooltip variant="outlined" arrow title={isParked ? "Unpark this invoice to mark it as ready" : ""}>
            <span>
              <Button
                disabled={isParked}
                loading={isTogglePostLoading}
                startDecorator={ableToPost ? <CheckCircleIcon /> : <CancelIcon />}
                color={ableToPost ? "primary" : "danger"}
                variant={ableToPost ? "solid" : "outlined"}
                onClick={openPostConfirm}
              >
                {ableToPost ? "Mark as ready" : "Unmark as ready"}
              </Button>
            </span>
          </Tooltip>
          <Button variant="outlined" color="neutral" onClick={goToPreviousInvoice}>
            <ArrowLeftIcon />
          </Button>

          <Button variant="outlined" color="neutral" onClick={goToNextInvoice}>
            <ArrowRightIcon />
          </Button>
        </Stack>
      </Stack>
      <Stack
        sx={{
          width: "100%",
          border: "1px solid #e0e0e0",
          borderRadius: 10,
        }}
      >
        {invoiceDetailsQueryClient.isRefetching ? (
          <Box sx={{ padding: "2rem" }}>
            <Loader />
          </Box>
        ) : (
          <Box padding={3}>
            <Grid container spacing={4} marginBottom={invoice?.privateRemarks ? 2 : -2}>
              {invoiceHeaderFields.map((field) => (
                <Grid xs={12} sm={4} lg={3} key={field.field}>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Stack
                      sx={{
                        width: "35px",
                        height: "35px",
                        backgroundColor: "#F1F5F9",
                        borderRadius: "10px",
                        padding: "8px",
                      }}
                    >
                      <field.Icon sx={{ height: "100%", width: "100%" }} />
                    </Stack>
                    <Stack>
                      <Typography fontSize={13} fontFamily={"Inter"} sx={{ color: "#64748B", fontWeight: 500 }}>
                        {field.field}
                      </Typography>
                      <Typography fontSize={15} sx={{ color: "#334155", fontWeight: 600 }}>
                        {field.access(invoiceDetailsQueryClient.data as GetInvoiceDetailsResponseObj)}
                      </Typography>
                    </Stack>
                  </Stack>
                </Grid>
              ))}
              <Grid xs={12} sm={4} lg={3}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Stack
                    sx={{
                      width: "35px",
                      height: "35px",
                      backgroundColor: "#F1F5F9",
                      borderRadius: "10px",
                      padding: "8px",
                    }}
                  >
                    <StarRateIcon sx={{ height: "100%", width: "100%" }} />
                  </Stack>
                  <Stack>
                    <Typography fontSize={13} fontFamily={"Inter"} sx={{ color: "#64748B", fontWeight: 500 }}>
                      Rising Star
                    </Typography>
                    <Typography fontSize={15} sx={{ color: "#334155", fontWeight: 600 }}>
                      {risingStarQuery.isLoading ? "Loading..." : risingStarQuery.data?.risingStarLevel ?? "_"}
                    </Typography>
                  </Stack>
                </Stack>
              </Grid>
            </Grid>
            {/*NOTE: These styles require higher specificity because MUI's default styles override them.*/}
            {invoice?.privateRemarks && (
              <Button
                onClick={() => setRemarksModalOpen(true)}
                variant="plain"
                style={{
                  backgroundColor: "transparent",
                }}
                sx={{ padding: "0" }}
              >
                Accounting Remarks
                <KeyboardArrowRightIcon sx={{ paddingTop: "3px", fontSize: "24px", marginLeft: "0" }} />
              </Button>
            )}
          </Box>
        )}
        <Modal open={remarksModalOpen} onClose={() => setRemarksModalOpen(false)}>
          <ModalDialog>
            <ModalClose />
            <DialogTitle>Invoice Remarks</DialogTitle>
            <Stack
              sx={{
                overflowY: "auto",
                overflowX: "hidden",
              }}
            >
              <Typography>
                <div dangerouslySetInnerHTML={{ __html: decodeAndFormatHtml(invoice?.privateRemarks || "") }}></div>
              </Typography>
            </Stack>
          </ModalDialog>
        </Modal>
        <Modal open={notesModalOpen} onClose={() => setNotesModalOpen(false)}>
          <ModalDialog sx={{ borderRadius: "md", p: 3, minWidth: 500 }}>
            <ModalClose />
            <DialogTitle>Internal Notes</DialogTitle>
            <DialogContent>
              <Typography fontSize={13} textColor="#64748B" mb={1}>
                Add notes for team members to see or for future reference
              </Typography>
              <Textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                minRows={6}
                maxRows={12}
                placeholder="Enter internal notes here..."
                sx={{ width: "100%" }}
              />
            </DialogContent>
            <DialogActions>
              <Button loading={isUpdatingNotes} variant="solid" color="primary" onClick={handleUpdateNotes}>
                Update Notes
              </Button>
              <Button variant="outlined" color="neutral" onClick={() => setNotesModalOpen(false)}>
                Cancel
              </Button>
            </DialogActions>
          </ModalDialog>
        </Modal>
        <Modal open={postConfirmOpen} onClose={() => setPostConfirmOpen(false)}>
          <ModalDialog sx={{ borderRadius: "md", p: 3 }}>
            <ModalClose />
            <DialogTitle>
              {ableToPost ? "Mark Invoice as Ready to Post" : "Unmark Invoice as Ready to Post"}
            </DialogTitle>
            <DialogContent>
              <Typography fontSize={15} textColor="#475569">
                {ableToPost
                  ? "This will mark the invoice as ready to be posted on CL. Are you sure you want to proceed?"
                  : "Are you sure you want to unmark this invoice as ready to post?"}
                <br />
              </Typography>

              {ableToPost && hasErrors && (
                <Alert color="danger" variant="soft" sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1 }}>
                  <WarningAmberIcon color="danger" />
                  <Typography fontSize={15} color="danger">
                    Validation errors were found. You can ignore errors and mark as ready to post if needed.
                  </Typography>
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                loading={isTogglePostLoading}
                variant={ableToPost ? "solid" : "outlined"}
                color={hasErrors && ableToPost ? "warning" : ableToPost ? "primary" : "danger"}
                onClick={() => handleConfirmPost()}
              >
                {hasErrors && ableToPost
                  ? "Ignore and mark as ready to post"
                  : ableToPost
                  ? "Mark as ready to post"
                  : "Unmark as ready to post"}
              </Button>
              <Button variant="outlined" color="neutral" onClick={() => setPostConfirmOpen(false)}>
                Cancel
              </Button>
            </DialogActions>
          </ModalDialog>
        </Modal>
      </Stack>
    </>
  );
}
